import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Clock, ArrowRight } from "lucide-react";
import { BLOG_POSTS, BLOG_CATEGORIES } from "@/data/content";

export default function Blog() {
  const [category, setCategory] = useState("All");

  const filtered = useMemo(() => {
    if (category === "All") return BLOG_POSTS;
    return BLOG_POSTS.filter((p) => p.category === category);
  }, [category]);

  return (
    <div data-testid="blog-page" className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 grid-bg radial-fade opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 relative">
          <p className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#00D4FF] mb-4">
            Blog & Insights
          </p>
          <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight text-white max-w-3xl leading-[1.05]">
            Ideas from the <span className="text-gradient-cyan">frontier</span> of AI engineering.
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
            Hard-won lessons, patterns and opinions from our work in AI, data
            platforms, cloud, and modern software.
          </p>
        </div>
      </section>

      {/* CATEGORIES + GRID */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-center gap-2 mb-10" data-testid="blog-categories">
            {BLOG_CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`blog-category-${c.toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-sm border transition ${
                  category === c
                    ? "bg-[#00D4FF] text-[#050B14] border-[#00D4FF] font-semibold"
                    : "bg-transparent text-slate-300 border-white/10 hover:border-[#00D4FF]/40 hover:text-white"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p, i) => (
              <motion.article
                key={p.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                data-testid={`blog-card-${p.slug}`}
                className="group rounded-2xl overflow-hidden bg-[#0A1128] border border-white/10 hover-glow"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={p.cover}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] via-transparent to-transparent" />
                  <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-[#050B14]/80 backdrop-blur-sm border border-[#00D4FF]/30 text-[10px] tracking-widest font-mono-tech uppercase text-[#00D4FF]">
                    {p.category}
                  </span>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                    <span>{p.date}</span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} /> {p.readTime}
                    </span>
                  </div>
                  <h3 className="font-display text-xl font-bold text-white leading-snug">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed line-clamp-3">
                    {p.excerpt}
                  </p>
                  <button
                    type="button"
                    data-testid={`blog-read-${p.slug}`}
                    className="mt-5 inline-flex items-center gap-1.5 text-[#00D4FF] hover:text-white text-sm font-semibold"
                  >
                    Read article <ArrowRight size={14} />
                  </button>
                </div>
              </motion.article>
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="text-slate-400 text-center py-20" data-testid="blog-empty">
              No articles in this category yet — check back soon.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
